import { ScrollCaptain } from 'scroll-captain';
import './image-text-mosaic.scss';

class ImageTextMosaic {
    constructor (element, options) {
        // Default options
        const defaults = {
            initAttr: 'data-imagetextmosaic',
            mediaEvenValue: 'media-even',
            mediaOddValue: 'media-odd',
            playPauseToggleValue: 'play-pause-toggle',
            videoStatus: 'data-imagetextmosaic-status',
            videoStateValue: 'video-state',
            breakpoint: 768
        };

        // Merge settings
        this.settings = Object.assign({}, defaults, options);

        // DOM Elements
        this.$root = element;
        this.$playButton = this.$root.querySelector(`[${this.settings.initAttr}="play"]`);
        this.$video = this.$root.querySelector('video');
        this.$playPauseToggle = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.playPauseToggleValue}"]`);
        this.$videoState = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.videoStateValue}"]`);
        this.$videoStatus = this.$root.querySelector(`[${this.settings.videoStatus}]`);

        // Options
        this.animation = null;
        this.breakpoint = window.matchMedia(`(min-width: ${this.settings.breakpoint}px)`);
        this.breakpointActive = this.breakpoint.matches;
    }

    initialize () {
        this.loadAnimation();
        this.setEvents();

        if (this.$playPauseToggle && this.$video) {
            this.toggleVideo();
            this.checkReducedMotion();
        }

        if (this.$playButton) {
            import('../../utilities/js/youtube-player').then(({ YoutubePlayer }) => {
                this.initYoutubePlayer(YoutubePlayer);
            });
        }
    }

    checkReducedMotion () {
        if (window.reducedMotionActive) {
            this.pauseHTML5Video();
        }
    }

    pauseHTML5Video () {
        this.$videoStatus.setAttribute(this.settings.videoStatus, 'paused');
        this.$video.pause();

        if (this.$videoState) {
            this.$videoState.textContent = window.translations.videoIsPaused;
        }
    }

    playHTML5Video () {
        this.$videoStatus.setAttribute(this.settings.videoStatus, 'played');
        this.$video.play();

        if (this.$videoState) {
            this.$videoState.textContent = window.translations.videoIsPlaying;
        }

    }

    initYoutubePlayer (YoutubePlayer) {
        const videoPlayer = new YoutubePlayer(this.$playButton, {
            eventScroller: window.eventScroller,
            autoplay: true,
            width: 1025,
            height: 567,
            onCall: () => {
                this.$root.classList.add('show--video');
            },
            onEnd: () => {
                if (videoPlayer.player.video.isMuted() === false || videoPlayer.mute === 0) {
                    videoPlayer.destroy();
                }
            }
        });
    }

    toggleVideo () {
        this.$playPauseToggle.addEventListener('click', () => {
            if (!this.$video.paused && !this.$video.ended && this.$video.readyState > 2) {
                this.pauseHTML5Video();
            } else {
                this.playHTML5Video();
            }
        });
    }

    loadAnimation () {
        this.animation = new ScrollCaptain(this.$root, {
            initAttr: this.settings.initAttr,
            animateMediaEven: {
                targetSuffix: this.settings.mediaEvenValue,
                onscroll: true,
                translateY: this.breakpoint.matches && !window.reducedMotionActive ? { 0: -35, 100: 35 } : null
            },
            animateMediaOdd: {
                targetSuffix: this.settings.mediaOddValue,
                onscroll: true,
                translateY: this.breakpoint.matches && !window.reducedMotionActive ? { 0: 35, 100: -35 } : null
            },
            updateOnResize: () => {
                if (this.breakpoint.matches !== this.breakpointActive) {
                    this.breakpointActive = this.breakpoint.matches;
                    this.updateAnimation(false);
                }
            }
        });
    }

    updateAnimation (updateRequired) {
        this.animation.defaultOptions.animateMediaEven.translateY = this.breakpoint.matches && !window.reducedMotionActive ? { 0: -35, 100: 35 } : null;
        this.animation.defaultOptions.animateMediaOdd.translateY = this.breakpoint.matches && !window.reducedMotionActive ? { 0: 35, 100: -35 } : null;

        if (updateRequired) {
            this.animation.update();
        }
    }

    setEvents () {
        window.addEventListener('motionPreferenceChanged', () => {
            this.updateAnimation(true);
        });
    }
}

export { ImageTextMosaic };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-imagetextmosaic="root"]');
        $$roots.forEach($root => {
            $root.mosaicAPI = new ImageTextMosaic($root);
            $root.mosaicAPI.initialize();
        });
    }
});
